import React from 'react';
import { Link } from 'react-router-dom';
import {
  Hammer,
  CheckCircle,
  Clock,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Zap,
  Shield,
  ArrowRight,
  Settings,
  Wrench,
  Calendar,
  MapPin,
  Building
} from 'lucide-react';

const CustomIndustryPage: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-600/10 to-gray-800/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <Hammer className="h-8 w-8 text-gray-600 mr-3" />
                <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Custom</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Tailored Solutions for Your Unique Business
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Get customized maintenance management solutions for specialized industries and unique business requirements with WorkOrder HQ™. 
                From niche markets to specialized operations, we adapt to your specific needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup?industry=8"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-600 bg-white dark:bg-gray-800 border-2 border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                >
                  View Pricing
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                14-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center">
                    <Settings className="h-8 w-8 text-gray-600 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Flexible Configuration</p>
                      <p className="text-gray-600 dark:text-gray-400">Adapt to your specific processes</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Wrench className="h-8 w-8 text-gray-500 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Custom Workflows</p>
                      <p className="text-gray-600 dark:text-gray-400">Tailored maintenance procedures</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Building className="h-8 w-8 text-gray-700 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Specialized Equipment</p>
                      <p className="text-gray-600 dark:text-gray-400">Unique asset management</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom-Specific Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Built for Unique Business Needs
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Everything you need to manage your specialized operations efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-gray-100 dark:bg-gray-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Settings className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Flexible Configuration
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Customize workflows, fields, and processes to match your unique business requirements and industry standards.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Wrench className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Specialized Equipment Tracking
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track unique equipment, specialized tools, and custom assets with tailored maintenance schedules.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Custom Scheduling
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Create specialized maintenance schedules that align with your unique operational requirements and constraints.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Industry-Specific Workflows
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Implement specialized procedures and workflows that match your industry's unique requirements and standards.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-red-100 dark:bg-red-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Compliance Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track specialized compliance requirements, certifications, and regulatory standards for your industry.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Custom Reporting
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Generate specialized reports and analytics tailored to your unique business metrics and KPIs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Custom Industry Applications
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              See how specialized businesses use WorkOrder HQ™ to streamline operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Use Case 1 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Hammer className="h-8 w-8 text-gray-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Specialized Services</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Manage unique service operations including specialized equipment, custom procedures, and niche market requirements.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Specialized equipment maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Custom workflow management
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Industry-specific compliance tracking
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Unique asset management
                </li>
              </ul>
            </div>

            {/* Use Case 2 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Settings className="h-8 w-8 text-gray-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Emerging Industries</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Adapt to new and emerging industries with flexible configuration and customizable features for evolving requirements.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Flexible system configuration
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Scalable solution architecture
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Custom integration capabilities
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Future-proof technology platform
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Custom Businesses Choose WorkOrder HQ™
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Adapt to Your Needs
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Customize the system to match your unique business processes and industry requirements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Scale with Growth
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Flexible architecture that grows with your business and adapts to changing requirements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Maintain Competitive Edge
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Optimize operations with tailored solutions that give you an advantage in specialized markets.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Future-Proof Technology
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Built on modern, flexible technology that adapts to new requirements and industry changes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg p-8 text-white">
              <div className="space-y-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">14 Days</p>
                    <p className="text-gray-100">Free Trial</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Up to 10</p>
                    <p className="text-gray-100">Technicians</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FileText className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Unlimited</p>
                    <p className="text-gray-100">Work Orders</p>
                  </div>
                </div>
                <Link
                  to="/signup?industry=8"
                  className="block w-full bg-white text-gray-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors mt-6"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-600 to-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Create Your Custom Solution?
          </h2>
          <p className="text-xl text-gray-100 mb-8">
            Join specialized businesses who trust WorkOrder HQ™ to provide tailored solutions for their unique requirements.
          </p>
          <Link
            to="/signup?industry=8"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-gray-600 bg-white rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <p className="mt-4 text-sm text-gray-100">
            No credit card required • Full access to all features
          </p>
        </div>
      </section>
    </div>
  );
};

export default CustomIndustryPage;
